import { z } from 'zod';
/**
 * The Financial Pulse contract.
 *
 * A card and an answer are the same object seen from two directions: a card is
 * a signal the system raised unprompted, an answer is one somebody asked for.
 * Both carry a computed number and the evidence behind it, and in both cases
 * any prose is a separate field written afterwards.
 *
 * That separation is the contract, not a detail of it. There is no shape here
 * in which a sentence and a figure arrive as one string, because that is the
 * shape in which a model's wording can silently become a number.
 */
export const PULSE_TIERS = ['urgent', 'costing', 'opportunity', 'fyi'];
export const pulseTierSchema = z.enum(PULSE_TIERS);
/** Defined by what the reader should do, never by how the news feels. */
export const PULSE_TIER_LABELS = {
    urgent: 'Needs action today',
    costing: 'Costing you money',
    opportunity: 'Money available',
    fyi: 'Worth knowing',
};
export const PULSE_TIER_ORDER = ['urgent', 'costing', 'opportunity', 'fyi'];
export const pulseCardSchema = z.object({
    key: z.string(),
    subjectKey: z.string(),
    tier: pulseTierSchema,
    /** One sentence, with the number already in it. */
    headline: z.string(),
    detail: z.string().nullable(),
    /** Money, as a string. Null when the signal is not about an amount. */
    amountMinor: z.string().nullable(),
    dueOn: z.string().nullable(),
    /** Where the number came from, so somebody can disagree with it. */
    evidence: z.record(z.string(), z.unknown()),
    score: z.number().int(),
});
export const pulseSchema = z.object({
    cards: z.array(pulseCardSchema),
    /** How many signals there were altogether, so the limit hides nothing. */
    totalSignals: z.number().int(),
    asOf: z.string(),
});
export const pulseQuerySchema = z.object({
    limit: z.coerce.number().int().min(1).max(20).optional(),
    asOf: z.string().optional(),
});
export const dismissPulseRequestSchema = z.strictObject({
    signalKey: z.string().min(1).max(60),
    subjectKey: z.string().max(200).optional(),
    /** Null snoozes indefinitely, subject to the signal growing materially. */
    snoozeDays: z.number().int().min(1).max(365).nullable().optional(),
    reason: z.string().trim().max(500).optional(),
});
/* -------------------------------------------------------------------------- */
/* Asking                                                                     */
/* -------------------------------------------------------------------------- */
/**
 * Everything this system can be asked.
 *
 * Six, because six can be tested. A system that answers anything answers
 * nothing dependably, and an owner who is wrong once about money stops
 * trusting the whole screen.
 */
export const QUESTION_INTENTS = [
    'spend_by_supplier',
    'spend_by_category',
    'large_transactions',
    'unreviewed_documents',
    'itc_summary',
    'tax_treatment',
    /**
     * "Show me the EMCO invoices from January." Not a total — a LIST. It runs
     * the same deterministic parser the Everything page uses, over the same
     * search spine, so the answer is a set of objects a person can open.
     */
    'find_objects',
];
export const questionIntentSchema = z.enum(QUESTION_INTENTS);
export const QUESTION_EXAMPLES = {
    spend_by_supplier: 'How much have we spent with EMCO this year?',
    spend_by_category: 'Where did the money go last quarter?',
    large_transactions: 'Show me everything over $5,000 since April.',
    find_objects: 'Show me EMCO invoices from January.',
    unreviewed_documents: 'What still needs reviewing?',
    itc_summary: 'How much HST can we claim this quarter?',
    tax_treatment: 'Why is this receipt treated the way it is?',
};
export const questionParamsSchema = z.object({
    from: z.string().optional(),
    to: z.string().optional(),
    subject: z.string().max(200).optional(),
    minAmountMinor: z.number().int().min(0).optional(),
    recordId: z.string().optional(),
});
export const askRequestSchema = z.strictObject({
    question: z.string().trim().min(3).max(500),
});
/**
 * The answer.
 *
 * `headline`, `totalMinor` and `rows` are computed in SQL. `narration` is
 * prose a model wrote about them afterwards, and it is nullable because an
 * answer without it is still a complete answer. Nothing reads `narration`
 * back, and no number in this object is derived from it.
 */
export const answerSchema = z.object({
    intent: questionIntentSchema,
    params: questionParamsSchema,
    headline: z.string(),
    totalMinor: z.string().nullable(),
    rows: z.array(z.record(z.string(), z.unknown())),
    evidence: z.record(z.string(), z.unknown()),
    narration: z.string().nullable(),
    narrationModel: z.string().nullable(),
    /**
     * The period the figures actually cover, and whether the question named it.
     *
     * When a question names no period the engine still has to pick one, and a
     * screen that does not say so answers a different question confidently.
     * `periodPhrase` is the person's own words when they gave them.
     */
    periodFrom: z.string(),
    periodTo: z.string(),
    periodAssumed: z.boolean(),
    periodPhrase: z.string().nullable(),
});
/** What comes back when the question is outside the menu. */
export const unanswerableSchema = z.object({
    understood: z.literal(false),
    /** Said plainly, with what it can answer instead. */
    message: z.string(),
    suggestions: z.array(z.string()),
});
export const askResponseSchema = z.union([
    answerSchema.extend({ understood: z.literal(true) }),
    unanswerableSchema,
]);
//# sourceMappingURL=pulse.js.map